import React, { useMemo, useState } from "react";
import { Alert, Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { Add, DeleteOutline, EditOutlined } from "@mui/icons-material";
import DataTable from "components/common/tables/DataTable";
import { AppButton } from "components/common/buttons";
import { AppConfirmDialog } from "components/common/dialogs";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { useAuthorization } from "auth/AuthorizationContext";
import { PERMISSIONS } from "auth/permissionCodes";
import { formatAmount, formatCurrencyWithUnit, formatQuantity } from "utils/formatters";
import { refreshAfterSave } from "utils/workflowOperations";
import { amountAfterVat, getPaymentVat } from "../../utils/contractVat";
import { deleteContractPayment, saveContractPayment } from "../../services/contractPayments.service";
import { groupContractPayments, indexPaymentItems, paymentIdOf } from "./paymentScopes";
import ContractPaymentModal from "./ContractPaymentModal";

export default function ContractPaymentProfileDetails({ profile, payments, allPayments, contractItems, sections, onChanged, loading }) {
  const { can } = useAuthorization();
  const canCreate = can(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_CREATE);
  const canUpdate = can(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_UPDATE);
  const canDelete = can(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_DELETE);
  const [dialog, setDialog] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const itemIndex = useMemo(() => indexPaymentItems(contractItems), [contractItems]);
  const groups = useMemo(() => groupContractPayments(payments, itemIndex, sections), [payments, itemIndex, sections]);
  const showSnackbar = (message, severity = "success") => setSnackbar({ open: true, message, severity });
  const handleSave = async (data, paymentId) => {
    if (!can(paymentId != null ? PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_UPDATE : PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_CREATE)) {
      throw new Error("Bạn không có quyền lưu chi tiết thanh toán.");
    }
    await saveContractPayment(data, paymentId);
    const refreshed = await refreshAfterSave(onChanged, showSnackbar);
    if (refreshed) showSnackbar(paymentId != null ? "Cập nhật chi tiết thanh toán thành công" : "Thêm chi tiết thanh toán thành công");
  };
  const handleDelete = async () => {
    if (deleting || !deleteTarget || !canDelete) return;
    setDeleting(true);
    try {
      await deleteContractPayment(paymentIdOf(deleteTarget));
      setDeleteTarget(null);
      if (await refreshAfterSave(onChanged, showSnackbar)) showSnackbar("Xóa chi tiết thanh toán thành công");
    } catch (error) {
      showSnackbar(error.response?.data?.message || error.message || "Không thể xóa chi tiết thanh toán.", "error");
    } finally { setDeleting(false); }
  };
  const columns = [
    { accessorKey: "itemCode", header: "Mã hạng mục", size: 100 },
    { accessorKey: "itemName", header: "Hạng mục", size: 240 },
    { accessorKey: "unit", header: "Đơn vị", size: 50 },
    { accessorKey: "paymentQuantity", header: "Khối lượng thanh toán", size: 80, Cell: ({ cell }) => formatQuantity(cell.getValue()) },
    { accessorKey: "unitPrice", header: "Đơn giá", size: 90, Cell: ({ cell }) => formatAmount(cell.getValue()) },
    { accessorKey: "paymentAmount", header: "Thành tiền", size: 110, Cell: ({ cell }) => formatAmount(cell.getValue()) },
    {
      id: "remaining", header: "Khối lượng còn lại", size: 80, Cell: ({ row }) => {
        const paid = allPayments.filter((payment) => payment.contractItemId === row.original.contractItemId)
          .reduce((sum, payment) => sum + Number(payment.paymentQuantity || 0), 0);
        return formatQuantity(Math.max(0, Number(itemIndex.get(row.original.contractItemId)?.contractQuantity || 0) - paid));
      }
    },
    { accessorKey: "notes", header: "Ghi chú" },
  ];
  return <Box sx={{ p: 2, bgcolor: "#f8fafc" }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1 }}>
      <Typography fontWeight={700}>Chi tiết {profile.paymentName}</Typography>
      {canCreate && <AppButton variant="outlined" size="small" startIcon={<Add />} disabled={loading}
        onClick={() => setDialog({ editingPayment: null })}>Thêm chi tiết</AppButton>}
    </Box>
    {!groups.length && <Alert severity="info">Chưa có chi tiết thanh toán cho kỳ này.</Alert>}
    {groups.map((section) => <Box key={section.id} sx={{ mb: 3 }} component="section" aria-label={section.name}>
      <Typography variant="subtitle1" fontWeight={750} sx={{ mb: 1, color: "primary.main", textTransform: "uppercase", fontSize: "15px" }}>{section.name}</Typography>
      {section.scopes.map((scope) => {
        const total = scope.items.reduce((sum, payment) => sum + payment.paymentAmount, 0);
        return <Box key={scope.id} sx={{ mb: 2 }} component="section" aria-label={`${section.name} / ${scope.name}`}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography fontWeight={650} fontSize="14px">{scope.name}</Typography>
            {scope.readOnly && <Chip label="Chỉ xem" size="small" />}
          </Box>
          <DataTable columns={columns} data={scope.items} getRowId={(row) => String(paymentIdOf(row))}
            enablePagination={false} enableBottomToolbar={false} enableGlobalFilter={false} state={{ isLoading: loading }}
            enableRowActions={!scope.readOnly && (canUpdate || canDelete)}
            renderRowActions={({ row }) => <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              {canUpdate && <Tooltip title="Chỉnh sửa chi tiết">
                <IconButton
                  size="small"
                  disabled={loading}
                  aria-label="Chỉnh sửa chi tiết"
                  onClick={() => setDialog({ editingPayment: row.original })}
                >
                  <EditOutlined fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>}
              {canDelete && <Tooltip title="Xóa chi tiết">
                <IconButton
                  size="small"
                  disabled={loading}
                  aria-label="Xóa chi tiết"
                  onClick={() => setDeleteTarget(row.original)}
                >
                  <DeleteOutline fontSize="small" color="error" />
                </IconButton>
              </Tooltip>}
            </Box>} />
          <Typography sx={{ mt: 1, textAlign: "right", fontSize: "13px" }}>
            Trước thuế: <b>{formatCurrencyWithUnit(total)}</b> - Sau thuế (<b>{getPaymentVat(profile)}%</b> VAT): <b>{formatCurrencyWithUnit(amountAfterVat(total, getPaymentVat(profile)))}</b>
          </Typography>
        </Box>;
      })}
    </Box>)}
    <ContractPaymentModal open={dialog !== null} onClose={() => setDialog(null)} onSave={handleSave}
      paymentProfile={profile} contractItems={contractItems} existingPayments={payments}
      allContractPayments={allPayments} editingPayment={dialog?.editingPayment || null} />
    <AppConfirmDialog open={deleteTarget !== null} title="Xóa chi tiết thanh toán" type="delete" loading={deleting}
      message={`Bạn có chắc chắn muốn xóa thanh toán của hạng mục "${deleteTarget?.itemName || ""}"?`}
      onClose={() => { if (!deleting) setDeleteTarget(null); }} onConfirm={handleDelete} />
    <AppSnackbar {...snackbar} onClose={() => setSnackbar((current) => ({ ...current, open: false }))} />
  </Box>;
}
